/**
 * React Query hooks for collection follow-ups (R3/R4).
 * Manages invoice-level follow-up scheduling, dashboard widget, and reminder settings.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

const API_BASE = import.meta.env.VITE_API_URL;
if (!API_BASE) {
  throw new Error('VITE_API_URL environment variable is required.');
}

async function getAuthToken(): Promise<string> {
  const { getAuth } = await import('firebase/auth');
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

async function fetchWithAuth(endpoint: string, options?: RequestInit) {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { detail?: string }).detail || response.statusText);
  }
  return response.json();
}

// ============================================================================
// Types
// ============================================================================

export interface CollectionFollowup {
  id: string;
  invoice_id: string;
  due_date: string;
  original_due_date: string;
  expected_amount_cents: number | null;
  note: string | null;
  status: 'pending' | 'done' | 'dismissed';
  installment_group_id: string | null;
  last_sent_at: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface FollowupCreate {
  due_date: string;
  expected_amount_cents: number | null;
  note?: string;
  installment_group_id?: string;
}

export interface FollowupPatch {
  status?: 'pending' | 'done' | 'dismissed';
  due_date?: string;
  note?: string;
}

export interface UpcomingFollowupItem {
  id: string;
  invoice_id: string;
  invoice_number: string;
  client_name: string | null;
  project_name: string | null;
  due_date: string;
  original_due_date: string;
  expected_amount_cents: number | null;
  note: string | null;
  is_overdue: boolean;
}

export interface CollectionReminderSettings {
  collection_reminders_enabled: boolean;
  collection_reminders_recipients: string;
}

// ============================================================================
// Query Hooks
// ============================================================================

export function useInvoiceFollowups(invoiceId: string | undefined) {
  return useQuery<CollectionFollowup[]>({
    queryKey: ['invoice-followups', invoiceId],
    queryFn: async () => {
      const data = await fetchWithAuth(`/api/invoices/${invoiceId}/followups`);
      return data.followups || [];
    },
    enabled: !!invoiceId,
  });
}

export function useUpcomingFollowups(limit = 5) {
  return useQuery<UpcomingFollowupItem[]>({
    queryKey: ['dashboard', 'upcoming-followups'],
    queryFn: async () => {
      const data = await fetchWithAuth(`/api/dashboard/upcoming-followups?limit=${limit}`);
      return data.followups || [];
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useCollectionReminderSettings() {
  return useQuery<CollectionReminderSettings>({
    queryKey: ['settings', 'collection-reminders'],
    queryFn: async () => {
      const data = await fetchWithAuth('/api/me/settings/collection-reminders');
      // API returns short keys (enabled, recipients); normalize to internal type
      return {
        collection_reminders_enabled: data.enabled ?? data.collection_reminders_enabled ?? false,
        collection_reminders_recipients: data.recipients ?? data.collection_reminders_recipients ?? '',
      };
    },
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useCreateFollowup(invoiceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: FollowupCreate | FollowupCreate[]) => {
      if (Array.isArray(data)) {
        // Multi-installment: POST each one sequentially
        return Promise.all(
          data.map((item) =>
            fetchWithAuth(`/api/invoices/${invoiceId}/followups`, {
              method: 'POST',
              body: JSON.stringify(item),
            })
          )
        );
      }
      return fetchWithAuth(`/api/invoices/${invoiceId}/followups`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoice-followups', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'upcoming-followups'] });
      const count = Array.isArray(variables) ? variables.length : 1;
      toast.success(count > 1 ? `${count} follow-ups scheduled` : 'Follow-up scheduled');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to schedule follow-up');
    },
  });
}

export function usePatchFollowup(invoiceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ followupId, data }: { followupId: string; data: FollowupPatch }) =>
      fetchWithAuth(`/api/invoices/${invoiceId}/followups/${followupId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onMutate: async ({ followupId, data }) => {
      await queryClient.cancelQueries({ queryKey: ['invoice-followups', invoiceId] });
      const prev = queryClient.getQueryData<CollectionFollowup[]>(['invoice-followups', invoiceId]);

      queryClient.setQueryData<CollectionFollowup[]>(['invoice-followups', invoiceId], (old) => {
        if (!old) return old;
        return old.map((f) =>
          f.id === followupId ? { ...f, ...data, updated_at: new Date().toISOString() } : f
        );
      });

      // Optimistically remove from dashboard widget if marking done/dismissed
      const prevDash = queryClient.getQueryData<UpcomingFollowupItem[]>(['dashboard', 'upcoming-followups']);
      if (data.status && data.status !== 'pending') {
        queryClient.setQueryData<UpcomingFollowupItem[]>(['dashboard', 'upcoming-followups'], (old) =>
          old ? old.filter((f) => f.id !== followupId) : old
        );
      }

      return { prev, prevDash };
    },
    onError: (_err, _vars, ctx) => {
      const context = ctx as { prev?: CollectionFollowup[]; prevDash?: UpcomingFollowupItem[] } | undefined;
      if (context?.prev) {
        queryClient.setQueryData(['invoice-followups', invoiceId], context.prev);
      }
      if (context?.prevDash) {
        queryClient.setQueryData(['dashboard', 'upcoming-followups'], context.prevDash);
      }
      toast.error('Failed to update follow-up');
    },
    onSuccess: () => {
      // Re-sync dashboard widget for snooze updates (due_date changed)
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'upcoming-followups'] });
    },
  });
}

export function useDeleteFollowup(invoiceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (followupId: string) =>
      fetchWithAuth(`/api/invoices/${invoiceId}/followups/${followupId}`, { method: 'DELETE' }),
    onMutate: async (followupId) => {
      await queryClient.cancelQueries({ queryKey: ['invoice-followups', invoiceId] });
      const prev = queryClient.getQueryData<CollectionFollowup[]>(['invoice-followups', invoiceId]);

      queryClient.setQueryData<CollectionFollowup[]>(['invoice-followups', invoiceId], (old) =>
        old ? old.filter((f) => f.id !== followupId) : old
      );
      queryClient.setQueryData<UpcomingFollowupItem[]>(['dashboard', 'upcoming-followups'], (old) =>
        old ? old.filter((f) => f.id !== followupId) : old
      );

      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      const context = ctx as { prev?: CollectionFollowup[] } | undefined;
      if (context?.prev) queryClient.setQueryData(['invoice-followups', invoiceId], context.prev);
      toast.error('Failed to remove follow-up');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'upcoming-followups'] });
    },
  });
}

export function useUpdateCollectionReminderSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<CollectionReminderSettings>) =>
      fetchWithAuth('/api/me/settings/collection-reminders', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'collection-reminders'] });
      toast.success('Collection reminder settings saved');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to save settings');
    },
  });
}
